import { memo, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { cn } from "../../ui/utils";
import type { ChartKind, ChartSpec } from "../types";
import { useComplianceAssistantStore } from "../store";
import { CHART_THEME_CLASS } from "./chartTheme";
import { CartesianChart } from "./CartesianChart";
import { ChatScanTimeline } from "./ChatScanTimeline";
import { ChatScatter } from "./ChatScatter";
import { RadialChart } from "./RadialChart";
import { ChartEmpty, ChartError, ChartNoData, ChartSkeleton } from "./ChartStates";
import { getPreferredKind, rememberPreferredKind } from "./chartPreference";
import {
  buildSrSummary,
  chartHeight,
  deriveState,
  isAllZero,
  isRadial,
  isScanTimeline,
  isScatter,
  kindLabel,
  normalizeChart,
} from "./resolveChart";

interface ChartMessageProps {
  spec?: ChartSpec;
  loading?: boolean;
  className?: string;
  /**
   * True when the block was inferred from a markdown table rather than produced by a platform chart
   * mapper. Such a chart is a rendering of numbers the assistant wrote in prose, not a reproduction
   * of a dashboard visualization, and it is labelled so the two are never mistaken for each other.
   */
  derivedFromTable?: boolean;
}

/** Title-cased menu label — "Bar Chart", "Horizontal Bar Chart". */
function alternativeLabel(kind: ChartKind): string {
  const base = kindLabel(kind);
  return `${base.charAt(0).toUpperCase()}${base.slice(1)} Chart`;
}

function ChartCard({
  title,
  subtitle,
  className,
  children,
}: {
  title?: string;
  subtitle?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <figure
      className={cn(
        // Same neutral-block surface + px-3 inset as AssistantTable, so charts and tables align as one family.
        "my-1.5 rounded-xl border border-border/50 bg-muted/30 px-3 py-3",
        "dark:border-white/[0.06] dark:bg-white/[0.02]",
        className,
      )}
    >
      {(title || subtitle) && (
        <figcaption className="mb-3">
          {title && <p className="text-xs font-semibold leading-snug text-foreground">{title}</p>}
          {subtitle && (
            <p className="mt-0.5 text-[0.6875rem] leading-snug text-muted-foreground">{subtitle}</p>
          )}
        </figcaption>
      )}
      {children}
    </figure>
  );
}

function useInView<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: "120px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  return { ref, inView };
}

/**
 * The "RegO Recommended Chart" caption and its one-line justification.
 *
 * The reason is the engine's, verbatim. It states a property of the data — how many categories,
 * whether they form a time axis, whether they are parts of a whole — so a reader can check it
 * against the chart in front of them rather than taking it on trust.
 */
function RecommendationHeader({ reason }: { reason: string }) {
  return (
    <div className="mb-2">
      <p className="text-[0.6875rem] font-semibold uppercase tracking-wide text-primary">
        RegO Recommended Chart
      </p>
      <p className="mt-0.5 text-[0.6875rem] leading-snug text-muted-foreground">{reason}</p>
    </div>
  );
}

/**
 * "Try another visualization" — one button per kind the backend judged honest for this data.
 *
 * Pressing one re-renders the block this component already holds with a different `kind`. No request
 * is made and no value is recalculated, so two renderings of one answer cannot disagree. Kinds the
 * data cannot carry are not here to be pressed: that decision was made server-side, where the
 * dashboard's own chart choices are known.
 */
function AlternativeSwitcher({
  kinds,
  active,
  onSelect,
}: {
  kinds: ChartKind[];
  active: ChartKind;
  onSelect: (kind: ChartKind) => void;
}) {
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1">
      <span className="mr-0.5 text-[0.6875rem] text-muted-foreground">Try another visualization:</span>
      {kinds.map((kind) => (
        <button
          key={kind}
          type="button"
          onClick={() => onSelect(kind)}
          aria-pressed={kind === active}
          className={cn(
            "inline-flex items-center rounded-md px-2 py-1 text-[0.6875rem] font-medium transition-colors",
            kind === active
              ? "bg-primary/10 text-primary"
              : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
          )}
        >
          {alternativeLabel(kind)}
        </button>
      ))}
    </div>
  );
}

export const ChartMessage = memo(function ChartMessage({
  spec,
  loading,
  className,
  derivedFromTable,
}: ChartMessageProps) {
  const conversationId = useComplianceAssistantStore((s) => s.conversationId);

  // Kinds the backend offered, plus the one it recommended, so the active button is always present.
  //
  // Scatter is its own island. Switching only swaps `kind` over identical rows, and a scatter's
  // rows are two measures with a numeric x — drawn as a bar they would be plotted against a
  // stringified axis, and a category chart's rows drawn as a scatter would place every point at
  // x=0. The backend already declines to pair them; this refuses the pairing a second time,
  // because the cost of getting it wrong is a chart that renders confidently and lies.
  const offered = useMemo<ChartKind[]>(() => {
    if (!spec?.reason || !spec.alternatives?.length) return [];
    const recommended = spec.kind && spec.kind !== "auto" ? [spec.kind] : [];
    const all = Array.from(new Set([...recommended, ...spec.alternatives]));
    return all.some(isScatter) ? all.filter(isScatter) : all.filter((k) => !isScatter(k));
  }, [spec?.reason, spec?.kind, spec?.alternatives]);

  // Seeded from the conversation's remembered choice, but only when that kind is one this dataset
  // can honestly carry — a preference must never resurrect a rendering the engine excluded.
  const [selected, setSelected] = useState<ChartKind | null>(null);
  useEffect(() => {
    const preferred = getPreferredKind(conversationId);
    setSelected(preferred && offered.includes(preferred) ? preferred : null);
  }, [conversationId, offered]);

  // Only `kind` is replaced. Same rows, same series, same title — so switching cannot change a value.
  const effectiveSpec = useMemo<ChartSpec | undefined>(
    () => (spec && selected && selected !== spec.kind ? { ...spec, kind: selected } : spec),
    [spec, selected],
  );

  const state = deriveState(effectiveSpec, loading);
  const normalized = useMemo(
    () => (state === "success" && effectiveSpec ? normalizeChart(effectiveSpec) : null),
    [state, effectiveSpec],
  );
  const { ref, inView } = useInView<HTMLDivElement>();

  // An all-zero chart has nothing to show — except on a scatter, where isAllZero only inspects the
  // y series. A drift scatter whose controls have all held steady has every y at zero and a spread
  // of real x values; treating that as empty would hide the answer rather than report it.
  const effective =
    state === "success" && normalized && !isScatter(normalized.kind)
      && !isScanTimeline(normalized.kind)
      && isAllZero(normalized)
      ? "empty"
      : state;

  if (effective === "loading") {
    return (
      <ChartCard title={spec?.title} subtitle={spec?.subtitle} className={className}>
        <ChartSkeleton />
      </ChartCard>
    );
  }
  if (effective === "error") {
    return (
      <ChartCard title={spec?.title} className={className}>
        <ChartError message={spec?.message} />
      </ChartCard>
    );
  }
  if (effective === "nodata") {
    return (
      <ChartCard title={spec?.title} className={className}>
        <ChartNoData message={spec?.message} />
      </ChartCard>
    );
  }
  if (effective === "empty") {
    return (
      <ChartCard title={spec?.title} className={className}>
        <ChartEmpty message={spec?.message} />
      </ChartCard>
    );
  }
  if (!normalized) return null;

  const radial = isRadial(normalized.kind);
  const scatter = isScatter(normalized.kind);
  const timeline = isScanTimeline(normalized.kind);
  const summary = buildSrSummary(normalized);
  // Reserve the chart's exact final height up front: the lazy skeleton and the real chart
  // occupy identical space, so the in-view swap never shifts the layout.
  const reservedHeight = chartHeight(normalized);

  const activeKind = normalized.kind;
  const selfLabelled = activeKind === "doughnut" || scatter || timeline;

  return (
    <ChartCard title={normalized.title} subtitle={normalized.subtitle} className={className}>
      {spec?.reason && <RecommendationHeader reason={spec.reason} />}
      {derivedFromTable && (
        <p className="mb-2 text-[0.6875rem] leading-snug text-muted-foreground">
          Derived from the table above.
        </p>
      )}
      {/* @container: renderers adapt to the chart's own width, not the viewport, so the layout is right inside the fixed-width panel. */}
      {/* The doughnut and the scatter are platform components, which expose themselves as the image
          and carry this same summary. Labelling the wrapper too would nest a second role="img". */}
      <div
        ref={ref}
        role={selfLabelled ? undefined : "img"}
        aria-label={selfLabelled ? undefined : summary}
        className={cn("@container relative", CHART_THEME_CLASS)}
        style={{ minHeight: reservedHeight }}
      >
        {inView ? (
          timeline ? (
            <ChatScanTimeline chart={normalized} />
          ) : scatter ? (
            <ChatScatter chart={normalized} />
          ) : radial ? (
            <RadialChart chart={normalized} />
          ) : (
            <CartesianChart chart={normalized} />
          )
        ) : (
          <ChartSkeleton variant={radial ? "radial" : "cartesian"} height={reservedHeight} />
        )}
      </div>
      {offered.length > 1 && (
        <AlternativeSwitcher
          kinds={offered}
          active={activeKind}
          onSelect={(kind) => {
            setSelected(kind);
            rememberPreferredKind(conversationId, kind);
          }}
        />
      )}
    </ChartCard>
  );
});
